import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useRatesApi } from "./useRatesApi";

const queryKeys = {
    rates: (propertyId, roomId) => ['rates', { propertyId, roomId }],
};

export const useRatesData = (propertyId, roomId) => {
    const { findRatesByRoom, createRate, updateRate, updateAllRates, deleteRate } = useRatesApi();

    const queryKey = queryKeys.rates(propertyId, roomId);
    const queryClient = useQueryClient();

    const cachedRates = queryClient.getQueryData(queryKey);

    const queryResults = useQuery({
        queryKey: queryKey,
        queryFn: () => findRatesByRoom(propertyId, roomId),
        staleTime: defaultStaleTime,
        enabled: !!propertyId && !!roomId,
    });

    const _createRate = async (planId, price) => {
        const createdRate = await createRate(propertyId, roomId, planId, price);
        const updatedRates = cachedRates ? [...cachedRates, createdRate] : [createdRate];
        queryClient.setQueryData(queryKey, updatedRates);
    };

    const _updateRate = async (planId, price) => {
        const updatedRate = await updateRate(propertyId, roomId, planId, price);
        const updatedRates = cachedRates?.map(cachedRate =>
            cachedRate.planId === planId ? updatedRate : cachedRate
        );
        queryClient.setQueryData(queryKey, updatedRates);
    };

    const _updateAllRates = async (rates) => {
        const updatedRates = await updateAllRates(propertyId, roomId, rates);
        queryClient.setQueryData(queryKey, updatedRates);
    };

    const _deleteRate = async (planId) => {
        await deleteRate(propertyId, roomId, planId);
        const updatedRates = cachedRates?.filter(cachedRate => cachedRate.planId !== planId);
        queryClient.setQueryData(queryKey, updatedRates);
    };

    return {
        queryResults,
        createRate: _createRate,
        updateRate: _updateRate,
        updateAllRates: _updateAllRates,
        deleteRate: _deleteRate,
    };
};
